import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { formatCurrency } from 'legacyfrontend/helpers/formatPrice';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useMemo } from 'react';

const WarningsModal = ({ open, onClose, warnings = [], onAccept, handleBackToResults }) => {
  const { background, text } = useColors();

  const backToResultsUrl = useMemo(() => handleBackToResults(), [handleBackToResults]);

  const getPriceChangeInfo = (warning) => {
    if (warning.id !== 'CHANGE_PRICE' || !warning.extensions) return null;

    const { originalAmount, currentAmount, currency } = warning.extensions;

    return {
      originalAmount,
      currentAmount,
      currency,
      difference: currentAmount - originalAmount,
      isIncrease: currentAmount > originalAmount,
    };
  };

  const handleCloseModal = () => {
    onClose();
    setTimeout(() => {
      window.location.href = backToResultsUrl;
    }, 300);
  };

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      onClose={onClose}
      open={open}
      disableScrollLock={false}
      slotProps={{
        backdrop: {
          style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: (theme) => theme.palette.warning.light,
          color: (theme) => theme.palette.warning.contrastText,
        }}
      >
        <WarningAmberIcon color='warning' sx={{ color: '#fff' }} />
        <Typography component='div' variant='h6'>
          {warnings[0]?.extensions?.title || 'Aviso importante'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3, paddingTop: '24px !important' }}>
        {warnings.map((warning, index) => {
          const priceChange = getPriceChangeInfo(warning);

          return (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography fontWeight='bold' gutterBottom variant='subtitle1'>
                {warning.short_name}
              </Typography>

              <Typography paragraph variant='body1'>
                {warning.description}
              </Typography>

              {priceChange && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack alignItems='flex-start' direction='row' spacing={1} sx={{ mb: 1 }}>
                    <InfoOutlinedIcon color='info' />
                    <Typography color='text.secondary' variant='body2'>
                      Detalle del cambio de precio:
                    </Typography>
                  </Stack>

                  <Typography sx={{ mb: 0.5 }} variant='body2'>
                    Precio original:{' '}
                    {formatCurrency(priceChange.originalAmount, priceChange.currency)}
                  </Typography>

                  <Typography sx={{ mb: 0.5 }} variant='body2'>
                    Precio actual: {formatCurrency(priceChange.currentAmount, priceChange.currency)}
                  </Typography>

                  <Typography
                    color={priceChange.isIncrease ? 'error.main' : 'success.main'}
                    fontWeight='bold'
                    variant='body2'
                  >
                    Diferencia: {priceChange.isIncrease ? '+' : ''}
                    {formatCurrency(priceChange.difference, priceChange.currency)}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
        <Typography color='text.secondary' sx={{ mt: 3 }} variant='body2'>
          Para continuar con la reserva debe aceptar el cambio de precio, o volver a los resultados
          para seleccionar otra opción.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          color='secondary'
          onClick={handleCloseModal}
          startIcon={<ArrowBackIosIcon />}
          sx={{
            borderColor: background === 'dark' ? text.primary : text.secondary,
            color: background === 'dark' ? text.primary : text.secondary,
            '&:hover': {
              borderColor: background === 'dark' ? text.primary : text.secondary,
              backgroundColor: 'transparent',
            },
          }}
          variant='outlined'
        >
          Volver a resultados
        </Button>

        <Button
          onClick={onAccept}
          sx={{
            backgroundColor: background,
            color: text,
            '&:hover': {
              backgroundColor: background,
              color: text,
              opacity: 0.7,
            },
          }}
          variant='contained'
        >
          Aceptar cambio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarningsModal;
